import { useParams, Navigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { PageMeta } from "@/components/PageMeta";
import { ContentPageShell, Prose } from "@/components/ContentPageShell";
import { getPost, POSTS } from "@/content/blog";

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPost(slug) : undefined;
  if (!post) return <Navigate to="/blog" replace />;

  const url = `https://tipomatic.app/blog/${post.slug}`;
  const others = POSTS.filter((p) => p.slug !== post.slug && p.lang === post.lang).slice(0, 3);

  return (
    <>
      <PageMeta title={`${post.title} — Tipomatic`} description={post.description} path={`/blog/${post.slug}`} />
      <Helmet>
        <html lang={post.lang} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.description,
            author: { "@type": "Person", name: post.author },
            datePublished: post.date,
            inLanguage: post.lang,
            mainEntityOfPage: url,
          })}
        </script>
      </Helmet>
      <ContentPageShell>
        <article>
          <div className="text-xs uppercase tracking-wider text-primary mb-3">
            {post.date} · {post.readingMinutes} min čtení · {post.author}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">{post.title}</h1>
          <Prose>
            <div dangerouslySetInnerHTML={{ __html: post.bodyHtml }} />
          </Prose>
        </article>

        {others.length > 0 && (
          <aside className="mt-16 pt-8 border-t border-white/10">
            <h2 className="text-lg font-semibold text-pitch-cream mb-4">Další články</h2>
            <ul className="space-y-2">
              {others.map((p) => (
                <li key={p.slug}>
                  <Link to={`/blog/${p.slug}`} className="text-primary hover:underline">
                    {p.title}
                  </Link>
                </li>
              ))}
            </ul>
          </aside>
        )}

        <div className="mt-8">
          <Link to="/blog" className="text-sm text-pitch-cream/60 hover:text-pitch-cream">
            ← Všechny články
          </Link>
        </div>
      </ContentPageShell>
    </>
  );
}
